// Defensive mappers for the Skill Registry endpoints — same shape-tolerant
// style as entities/owner/model/toOwnerView.ts. Kept defensive (not
// assertive) because the backend contract may still shift field names
// while both sides land in parallel (see services/skills.ts header).

import type {
  AgentSkillCandidate,
  AgentSkillList,
  Skill,
  SkillAttachment,
  SkillAttachmentScope,
  SkillAttachmentStatus,
  SkillDetail,
  SkillShare,
  SkillSharePermission,
  SkillUserPermission,
  SkillVersion,
} from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

const ATTACHMENT_SCOPES: ReadonlySet<string> = new Set(['agent', 'argus_only', 'all']);
const ATTACHMENT_STATUSES: ReadonlySet<string> = new Set([
  'pending',
  'approved',
  'rejected',
  'revoked',
]);
const SHARE_PERMISSIONS: ReadonlySet<string> = new Set(['view', 'contribute']);
const USER_PERMISSIONS: ReadonlySet<string> = new Set(['owner', 'contribute', 'view']);

export function toSkill(raw: unknown): Skill | null {
  if (!isRecord(raw)) return null;
  const id = raw.id;
  if (typeof id !== 'string' || id.length === 0) return null;
  return {
    id,
    name: asString(raw.name),
    slug: asString(raw.slug),
    description: asString(raw.description),
    ownerId: asString(raw.owner_id),
    createdAt: asString(raw.created_at),
    updatedAt: asString(raw.updated_at),
    currentUserPermission: (
      USER_PERMISSIONS.has(asString(raw.current_user_permission))
        ? asString(raw.current_user_permission)
        : 'view'
    ) as SkillUserPermission,
  };
}

export function toSkillList(raw: unknown): Skill[] {
  const list = isRecord(raw) && Array.isArray(raw.skills) ? raw.skills : Array.isArray(raw) ? raw : [];
  return list.map(toSkill).filter((s): s is Skill => s !== null);
}

export function toAgentSkillCandidate(raw: unknown): AgentSkillCandidate | null {
  const skill = toSkill(raw);
  if (skill === null || !isRecord(raw)) return null;
  return {
    ...skill,
    attachment: toSkillAttachment(raw.attachment),
  };
}

export function toAgentSkillList(raw: unknown): AgentSkillList {
  const skills = isRecord(raw) && Array.isArray(raw.skills) ? raw.skills : [];
  return {
    agentId: isRecord(raw) ? asString(raw.agent_id) : '',
    skills: skills
      .map(toAgentSkillCandidate)
      .filter((skill): skill is AgentSkillCandidate => skill !== null),
  };
}

export function toSkillVersion(raw: unknown): SkillVersion | null {
  if (!isRecord(raw)) return null;
  const id = raw.id;
  const skillId = raw.skill_id;
  if (typeof id !== 'string' || id.length === 0) return null;
  if (typeof skillId !== 'string' || skillId.length === 0) return null;
  return {
    id,
    skillId,
    versionNumber: asNumber(raw.version_number, 1),
    skillMdContent: asString(raw.skill_md_content),
    assetsSubpath: asNullableString(raw.assets_subpath),
    isActive: raw.is_active === true,
    createdBy: asString(raw.created_by),
    sourceVersionId: asNullableString(raw.source_version_id),
    changeSummary: asNullableString(raw.change_summary),
    createdAt: asString(raw.created_at),
  };
}

export function toSkillAttachment(raw: unknown): SkillAttachment | null {
  if (!isRecord(raw)) return null;
  const id = raw.id;
  const skillId = raw.skill_id;
  if (typeof id !== 'string' || id.length === 0) return null;
  if (typeof skillId !== 'string' || skillId.length === 0) return null;
  const scopeRaw = asString(raw.scope);
  const statusRaw = asString(raw.status);
  return {
    id,
    skillId,
    scope: (ATTACHMENT_SCOPES.has(scopeRaw) ? scopeRaw : 'agent') as SkillAttachmentScope,
    agentId: asNullableString(raw.agent_id),
    status: (ATTACHMENT_STATUSES.has(statusRaw) ? statusRaw : 'pending') as SkillAttachmentStatus,
    requestedBy: asString(raw.requested_by),
    approvedBy: asNullableString(raw.approved_by),
    createdAt: asString(raw.created_at),
    decidedAt: asNullableString(raw.decided_at),
  };
}

export function toSkillAttachmentList(raw: unknown): SkillAttachment[] {
  const list = isRecord(raw) && Array.isArray(raw.attachments) ? raw.attachments : Array.isArray(raw) ? raw : [];
  return list.map(toSkillAttachment).filter((a): a is SkillAttachment => a !== null);
}

export function toSkillShare(raw: unknown): SkillShare | null {
  if (!isRecord(raw)) return null;
  const id = raw.id;
  const skillId = raw.skill_id;
  if (typeof id !== 'string' || id.length === 0) return null;
  if (typeof skillId !== 'string' || skillId.length === 0) return null;
  const permissionRaw = asString(raw.permission);
  return {
    id,
    skillId,
    userId: asString(raw.user_id),
    permission: (SHARE_PERMISSIONS.has(permissionRaw) ? permissionRaw : 'view') as SkillSharePermission,
    grantedBy: asString(raw.granted_by),
    createdAt: asString(raw.created_at),
  };
}

/** `GET /skills/{id}` (`SkillResponse`) — skill row + its active version only. */
export function toSkillDetail(raw: unknown): SkillDetail | null {
  const skill = toSkill(raw);
  if (skill === null || !isRecord(raw)) return null;
  return {
    ...skill,
    activeVersion: toSkillVersion(raw.active_version),
  };
}
